<template>
  <base-with-sidebar
    ref="base"
    title="Deal Summary"
    :formData="fullFormData"
    :mapper="mapper"
    :objectHandler="objectHandler"
    :arrayHandler="arrayHandler"
    :currValidationResult="currValidationResult"
  >
    <header class="spaced-header bg-light">
      <h3 class="text-center text-light-blue">
        {{ title }}
      </h3>
    </header>
    <b-row>
      <b-col>
        <form-wizard
          color="#3277b3"
          @on-complete="$emit('form-completed', formData)"
          @on-change="onChange"
          @on-validate="onValidate"
          subtitle=""
          error-color="#a94442"
          ref="wizard"
        >
          <h4 slot="title" class="sr-only">New Deal</h4>
          <tab-content title="Transaction Type">
            <card-list
              :title="basicTypes.transactionType.title"
              :items="basicTypes.transactionType.cards"
              v-on:cardSelected="onCardSelected"
              v-bind:selectedValue="formData.transactionType.name">
            </card-list>
          </tab-content>
          <tab-content title="Deal Type" :before-change="validateDealType">
            <card-list
              :title="basicTypes.dealType.title"
              :items="basicTypes.dealType.cards"
              v-on:cardSelected="onCardSelected"
              v-bind:selectedValue="formData.dealType.name">
            </card-list>
            <b-row v-if="formData.dealType.name === 'other'">
              <b-col md="8" lg="6" offset-md="2" offset-lg="3">
                <vue-form-generator
                  :model="formData"
                  :schema="dealTypeOther.schema"
                  :options="dealTypeOther.options"
                  ref="dealOtherForm"
                ></vue-form-generator>
              </b-col>
            </b-row>
          </tab-content>
          <tab-content title="Property Type">
            <card-list
              :title="basicTypes.propertyType.title"
              :items="basicTypes.propertyType.cards"
              v-on:cardSelected="onCardSelected"
              v-bind:selectedValue="formData.propertyType.name">
            </card-list>
          </tab-content>

          <slot></slot>
          <tab-content title="Upload Documents">
            <upload-section
              @files-changed="updateFiles"
            />
          </tab-content>
          <template slot="footer" slot-scope="props">
            <b-col class="text-center mt-3">
              <wizard-button
                v-if="props.activeTabIndex > 0"
                class="d-inline mr-3 previous-button"
                @click.native="props.prevTab()">
                <font-awesome-icon icon="chevron-left" />
                  Previous
              </wizard-button>
              <wizard-button
                v-if="shouldDisplayNext"
                class="d-inline mr-3 next-button"
                id="basic-deal-wizard-next-button"
                @click.native="props.nextTab()"
                :style="props.fillButtonStyle">
                  Next
                  <font-awesome-icon icon="chevron-right" />
              </wizard-button>
              <wizard-button
                v-if="props.isLastStep"
                class="d-inline mr-3 finish-button"
                @click.native="props.nextTab()"
                id="btn-deal-done"
                :style="props.fillButtonStyle"
              >
                  <font-awesome-icon icon="check" />
                  Done
              </wizard-button>
            </b-col>
          </template>
        </form-wizard>
      </b-col>
    </b-row>
  </base-with-sidebar>
</template>

<script>
import jshelper from '@/libs/helper';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faChevronLeft, faChevronRight, faCheck } from '@fortawesome/free-solid-svg-icons';
import { FormWizard, TabContent, WizardButton } from 'vue-form-wizard';
import 'vue-form-wizard/dist/vue-form-wizard.min.css';
import VueFormGenerator from 'vue-form-generator';
import CardList from '@/components/utils/CardList';
import BaseWithSidebar from '@/components/base/sidebar/components/BaseWithSidebar';

import formDataTitleMapper from '@/components/inquiry/resources/titleMapper.json';
import basicData from '@/components/new_deal/resources/basicData.json';
import noLendStates from '@/components/utils/address/resources/noLendStates.json';
import UploadSection from '@/components/new_deal/components/upload/UploadSection';


library.add(faChevronLeft, faChevronRight, faCheck);

function processConvertItems(key, value) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(value);
    reader.onload = () => resolve([key, reader.result]);
  });
}

export default {
  components: {
    UploadSection,
    'vue-form-generator': VueFormGenerator.component,
    FormWizard,
    TabContent,
    WizardButton,
    CardList,
    BaseWithSidebar,
  },
  props: {
    title: {
      type: String,
      default() { return 'Let\'s build your deal'; },
    },
    model: {
      type: Object,
      default() { return {}; },
    },
    mapper: {
      type: Object,
      default() { return {}; },
    },
    objectHandler: Function,
    arrayHandler: Function,
  },
  data() {
    return {
      statesToRemove: noLendStates,
      isMounted: false,
      prefix: 'property-',
      customValidation: {
        dealTypeDescription: {
          min: 5,
        },
      },
      currValidationResult: null,
      formTitleMapper: formDataTitleMapper,
      formData: jshelper.copy(basicData),
    };
  },
  mounted() {
    this.isMounted = true;
  },
  computed: {
    shouldDisplayNext() {
      /*
      * Should display the next button if the wizard is not in the last tab and:
      *   - Active tab is less than 3 (the number of "card tabs"), or
      *   - Active tab is 1 and dealTypeDescription has an input with minimum length.
      *     Note: The dealTypeDescription field is only shown if dealType is "other".
      *     Otherwise, it won't be shown as the handler for the card click will navigate to the
      *     next tab automatically.
      */
      if (!this.isMounted) { return false; }

      const wizard = this.$refs.wizard;
      if (!wizard) { return false; }

      const activeTab = wizard.activeTabIndex;
      const dealTypeDesc = this.formData.dealType.description;
      const minDescAllowed = this.customValidation.dealTypeDescription.min;
      const isDescValid = dealTypeDesc && dealTypeDesc.length >= minDescAllowed;

      return (!wizard.isLastStep && (activeTab > 2 || (activeTab === 1 && isDescValid)));
    },
    basicTypes() {
      const $this = this;
      const types = {
        transactionType: {
          title: 'Is this a Purchase or Refinance?',
          cards: $this.getTransactionCards(),
        },
        dealType: {
          title: 'What type of financing are you looking to obtain?',
          cards: $this.getDealCards(),
        },
        propertyType: {
          title: 'What type of property are you financing?',
          cards: $this.getPropertyCards(),
        },
      };

      return types;
    },
    dealTypeOther() {
      const minLength = this.customValidation.dealTypeDescription.min;
      return {
        schema: {
          fields: [{
            type: 'input',
            inputType: 'text',
            model: 'dealType.description',
            placeholder: 'Please describe your deal.',
            required: true,
            min: minLength,
            validator: VueFormGenerator.validators.string,
            set: (model, newValue) => {
              const dealModel = model;
              dealModel.dealType.description = jshelper.decodeHtml(newValue);
            },
          }],
        },
        options: {
          validationErrorClass: 'invalid-input',
          validateAfterChanged: true,
          fieldIdPrefix: 'deal-',
        },
      };
    },
    fullFormData() {
      const fullData = {};
      jshelper.map(this.formData, fullData, true);
      jshelper.map(this.$props.model, fullData, true);
      return fullData;
    },
  },
  methods: {
    enterKeyupHandler() {
      const noOpenedModals = document.querySelectorAll('.modal.show').length === 0;
      const textAreaNL = document.getElementsByTagName('textarea');
      const textAreaList = Array.prototype.slice.call(textAreaNL);
      const textAreaFocused = textAreaList.some(el => el === document.activeElement);
      if (noOpenedModals && !textAreaFocused) {
        this.$refs.wizard.nextTab();
      }
    },
    onChange(prevIndex, nextIndex) {
      this.$emit('form-changed', prevIndex, nextIndex);
      this.$root.$scroller.to(0);
    },
    onValidate(validationResult, activeTabIndex) {
      this.currValidationResult = {
        index: activeTabIndex,
        isValid: validationResult,
      };
    },
    onCardSelected(item) {
      const wizard = this.$refs.wizard;
      if (wizard) {
        if (wizard.activeTabIndex === 0) {
          this.$emit('transaction-type-changed', item.value);
        } else if (wizard.activeTabIndex === 1) {
          if (this.formData[item.name].name === item.value && item.value === 'other') { return; }

          this.formData.dealType.description = null;
          if (this.formData.estimateInfo) {
            this.formData.estimateInfo = {
              value: null,
              repairs: null,
              finalCost: null,
              flipped: null,
            };
          }
          this.$emit('deal-type-changed', item.value);
        }

        this.formData[item.name].name = item.value;
        if (item.value !== 'other') {
          this.currValidationResult = {
            index: wizard.activeTabIndex,
            isValid: true,
          };
          setTimeout(() => this.$refs.wizard.nextTab(), 100);
        }
      }
    },
    validateDealType() {
      const dealOtherForm = this.$refs.dealOtherForm;
      if (dealOtherForm) {
        return this.$refs.dealOtherForm.validate();
      }

      return true;
    },
    getTransactionCards() {
      const transactionCards = [
        {
          img: './static/inquiry/purchase.png',
          description: null,
          name: 'transactionType',
          value: 'purchase',
        },
        {
          img: './static/inquiry/refinance.png',
          description: null,
          name: 'transactionType',
          value: 'refinance',
        },
      ];

      return transactionCards;
    },
    getDealCards() {
      const dealCards = [
        {
          img: './static/inquiry/rehabff.png',
          description: null,
          name: 'dealType',
          value: 'rehab_fix_and_flip',
        },
        {
          img: './static/inquiry/new-construction.png',
          description: null,
          name: 'dealType',
          value: 'new_construction',
        },
        {
          img: './static/inquiry/non-rehab-bridge.png',
          description: null,
          name: 'dealType',
          value: 'non_rehab_bridge',
        },
        {
          img: './static/inquiry/other.png',
          description: null,
          name: 'dealType',
          value: 'other',
        },
      ];

      return dealCards;
    },
    getPropertyCards() {
      const cards = [
        {
          img: './static/inquiry/single-family.png',
          description: null,
          name: 'propertyType',
          value: 'single_family',
        },
        {
          img: './static/inquiry/multi-unit.png',
          description: null,
          name: 'propertyType',
          value: 'multi_unit',
        },
        {
          img: './static/inquiry/condo.png',
          description: null,
          name: 'propertyType',
          value: 'condo',
        },
      ];

      if (['new_construction', 'other'].includes(this.formData.dealType.name)) {
        cards.push(
          {
            img: './static/inquiry/commercial.png',
            description: null,
            name: 'propertyType',
            value: 'commercial',
          },
          {
            img: './static/inquiry/land.png',
            description: null,
            name: 'propertyType',
            value: 'land',
          },
        );
      }

      return cards;
    },
    async updateFiles(files) {
      const fileList = [];
      // eslint-disable-next-line no-restricted-syntax
      for (const key in files) {
        if (files[key] instanceof File) {
          // eslint-disable-next-line no-unused-vars,no-await-in-loop
          const [fileKey, fileBase64Str] = await processConvertItems(key, files[key]);
          const file = {};
          file.file = fileBase64Str;
          file['file-filename'] = files[key].name;
          fileList.push(file);
        }
      }
      this.model.files = fileList;
    },
  },
};
</script>

<style scoped>
  .position-relative {
    position: relative !important;
  }

  .sidebar-toggle {
    position: absolute !important;
    margin-left: 8px !important;
    margin-top: -37px !important;
    font-size: 14px;
  }

  .stepTitle active {
    color: #275d8b !important;
  }
</style>



// WEBPACK FOOTER //
// src/components/new_deal/components/BasicDeal.vue